'use client';

import { ArticleCard } from '@/sanity/fetchArticles';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import 'swiper/css/mousewheel';
import '@/styles/vendor/swiper.css';
import { Scrollbar, Mousewheel } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import SingleHighlightedArticleCard from '../base/SingleHighlightedArticleCard';
import Skeleton from '../base/Skeleton';
import { useEffect, useState } from 'react';
import { useBreakpoints } from '@/utils/useBreakpoints';
import { updateSlidesOffset } from '@/utils/updateSlidesOffset';
import { renderInlineMarkup } from '@/utils/renderInlineMarkup';
import layoutStyles from '@/styles/section-layouts.module.scss';
import styles from './ArticlesHighlightsSlider.module.scss';

export default function ArticlesHighlightsSlider({
  articles,
  title,
  className = '',
}: {
  articles?: ArticleCard[];
  title?: string;
  className?: string;
}) {
  const [slidesOffset, setSlidesOffset] = useState(100);
  const [showSkeleton, setShowSkeleton] = useState(true);
  const { isLg } = useBreakpoints(); // Custom hook to determine screen size

  const [MouseWheelEnabled, setMouseWheelEnabled] = useState(true);

  useEffect(() => {
    // Update slides offset initially and on window resize
    updateSlidesOffset(setSlidesOffset);
    const handleResize = () => updateSlidesOffset(setSlidesOffset);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize); // Cleanup event listener on component unmount
    };
  }, []);

  useEffect(() => {
    // Show loading skeleton for a brief period
    const timer = setTimeout(() => {
      setShowSkeleton(false);
    }, 200);
    return () => clearTimeout(timer); // Cleanup timer on component unmount
  }, []);

  return (
    <div className={className}>
      {title && (
        <div className={[styles.header, layoutStyles.containerContent].join(' ')}>
          <h1 className={styles.headerTitle}>{renderInlineMarkup(title)}</h1>
        </div>
      )}
      {showSkeleton && <LoadingSkeleton />} {/* Display loading skeleton */}
      {!showSkeleton && articles && articles?.length > 0 && (
        <Swiper
          slidesPerView="auto"
          scrollbar={{
            hide: false,
            el: '.articlesHighlights-swiper-scrollbar',
            draggable: true,
          }}
          modules={[Scrollbar, Mousewheel]}
          mousewheel={{
            forceToAxis: true,
          }}
          onLock={() => setMouseWheelEnabled(false)}
          onUnlock={() => setMouseWheelEnabled(true)}
          spaceBetween={isLg ? 32 : 16} // Adjust space between slides based on screen size
          slidesOffsetBefore={slidesOffset} // Set slides offset before
          slidesOffsetAfter={slidesOffset} // Set slides offset after
          className={!MouseWheelEnabled ? 'swiper-no-mousewheel' : undefined}
        >
          {articles?.map((article: ArticleCard) => (
            <SwiperSlide
              key={article._id}
              className={[styles.slide, 'articlesHighlights-swiper-slide'].join(' ')}
            >
              <SingleHighlightedArticleCard article={article} key={article._id} />
            </SwiperSlide>
          ))}
        </Swiper>
      )}
      <div className={[styles.scrollbarWrapper, layoutStyles.containerContent].join(' ')}>
        {!showSkeleton ? (
          <div
            className={['articlesHighlights-swiper-scrollbar', styles.scrollbar].join(
              ' ',
            )}
          ></div> // Custom scrollbar for large screens
        ) : (
          <div className={styles.scrollbarPlaceholder} />
        )}
      </div>
    </div>
  );
}

// Loading skeleton component to show a placeholder while articles are being fetched
function LoadingSkeleton() {
  return (
    <div className={[styles.skeletonWrapper, layoutStyles.containerContent].join(' ')}>
      <Skeleton className={styles.skeletonCard} />
      <Skeleton className={styles.skeletonCard} />
    </div>
  );
}
